// node_modules
import { FC, useCallback, useEffect, useState } from "react";
// Components
import { DeleteButton, EditableMarkdown, FindestEditableSpan } from "Components";
// Types
import { TTechnology } from "Types";
// Custom Hooks
import { useDebounce } from "Hooks";
// Enums
import { ToastTypeEnum } from "Enums";
// Helpers
import { ToastHelperSingleton } from "Helpers";
// Controllers
import { TechnologyControllerSingleton } from "Controllers";

export type TCaseKnownDirectionProps = {
    isEditMode: boolean,
    technology: TTechnology,
    technologyDeletedHandler?: (technologyDeletedId: number) => void
};

export const CaseKnownDirection: FC<TCaseKnownDirectionProps> = ({isEditMode, technology, technologyDeletedHandler}: TCaseKnownDirectionProps) => {
    // State
    const [currentTechnology, setCurrentTechnology] = useState<TTechnology | undefined>(undefined);
    const [technologyName, setTechnologyName] = useState<string | undefined>(undefined);
    const [technologyDescription, setTechnologyDescription] = useState<string | undefined>(undefined);

    // Const
    const debouncedTechnologyName = useDebounce(technologyName, 500);
    const debouncedTechnologyDescription = useDebounce(technologyDescription, 500);

    // Logic
    const updateTechnologyAsync = useCallback(async(newTechnologyName: string, newTechnologyDescription: string): Promise<void> => {
        // safety-checks
        if (newTechnologyName === null || newTechnologyName === undefined ||
            newTechnologyDescription === null || newTechnologyDescription === undefined) { return; }

        // call server to update technology
        technology.name = newTechnologyName;
        technology.description = newTechnologyDescription;
        const isSuccess: boolean = await TechnologyControllerSingleton
            .updateTechnologyAsync(technology);

        // tell user about update result 
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to update known direction.");
        } else {
            ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Successfully updated known direction.");
        }
        
        return;
    }, [technology]);

    const deleteTechnologyAsync = async(): Promise<void> => {
        // call server to delete technology
        const isSuccess: boolean = await TechnologyControllerSingleton
            .deleteTechnologyAsync(technology.id);

        // tell user about delete result 
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to delete known direction.");
        } else {
            ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Successfully deleted known direction.");
        }

        // call parent deletion handler
        if (technologyDeletedHandler) { technologyDeletedHandler(technology.id); }
    };

    useEffect(() => {
        setTechnologyName(undefined);
        setTechnologyDescription(undefined);
        if (technology) {
            setCurrentTechnology(technology);
            return;
        }
        setCurrentTechnology(undefined);
    }, [technology]);

    useEffect(() => {
        // do not trigger edit at first render, when switching to a new technology
        // or when displaying a new technology
        if (!currentTechnology || debouncedTechnologyName === null || debouncedTechnologyName === undefined) { return; }
        
        setTechnologyName(undefined);
        currentTechnology.name = debouncedTechnologyName;
        setCurrentTechnology(currentTechnology);

        // send technology update to the server
        updateTechnologyAsync(debouncedTechnologyName, currentTechnology.description ?? "");
    }, [currentTechnology, debouncedTechnologyName, updateTechnologyAsync]);

    useEffect(() => {
        // do not trigger edit at first render, when switching to a new technology
        // or when displaying a new technology
        if (!currentTechnology || debouncedTechnologyDescription === null || debouncedTechnologyDescription === undefined) { return; }

        setTechnologyDescription(undefined);
        currentTechnology.description = debouncedTechnologyDescription;
        setCurrentTechnology(currentTechnology);

        // send technology update to the server
        updateTechnologyAsync(currentTechnology.name, debouncedTechnologyDescription);
    }, [currentTechnology, debouncedTechnologyDescription, updateTechnologyAsync]);
    
    // Render
    return (
        <>
            {isEditMode && currentTechnology && technologyDeletedHandler ?
                    <div>
                        <FindestEditableSpan
                            entityId={technology.id}
                            text={currentTechnology.name}
                            onChangeText={(text: string) => setTechnologyName(text)}
                            />
                        <EditableMarkdown 
                            source={currentTechnology.description ? currentTechnology.description : ""}
                            noSource={"No known direction description available."}
                            isEditable={true}
                            handleSourceEdited={setTechnologyDescription} 
                            entityId={technology.toString()} />
                        <DeleteButton
                            onClick={deleteTechnologyAsync}/>
                    </div>
                :
                    <li>{technology.name}</li>
            }
        </>
    );
};
